import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  NoteAdd as RequestIcon,
} from '@mui/icons-material';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_SLOTS,
  COMPLETE_CATEGORY_PDF_SLOT,
} from '../../config/constants';
import { getDocuments, mergeAll } from '../../services/documentService';
import usePermissions from '../../hooks/usePermissions';
import DocumentCategoryCard from './DocumentCategoryCard';
import MergeButton from './MergeButton';
import RequestDocumentDialog from './RequestDocumentDialog';
import LoadingSpinner from '../common/LoadingSpinner';

const allCategories = Object.values(DOCUMENT_CATEGORIES);

const DocumentCategoryGrid = ({ studentId, student }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mergingAll, setMergingAll] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { canMergeDocuments, canUploadDocuments } = usePermissions();

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDocuments(studentId);
      setDocuments(response.data?.data?.documents || []);
    } catch (err) {
      setError('Failed to load documents. Please try again.');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const groupedDocs = allCategories.reduce((acc, cat) => {
    acc[cat] = documents.filter((doc) => doc.category === cat);
    return acc;
  }, {});

  const getCatCount = (cat) => {
    const catDocs = groupedDocs[cat] || [];
    const hasCompletePdf = catDocs.some((d) => d.slotName === COMPLETE_CATEGORY_PDF_SLOT);
    const slotDocs = catDocs.filter((d) => d.slotName !== COMPLETE_CATEGORY_PDF_SLOT);
    if (cat === 'extra_documents') return { count: slotDocs.length, hasCompletePdf };
    const slots = DOCUMENT_SLOTS[cat] || [];
    const bySlot = {};
    slotDocs.forEach((d) => { if (d.slotName) bySlot[d.slotName] = true; });
    return { uploaded: Object.keys(bySlot).length, total: slots.length, hasCompletePdf };
  };

  const handleMergeAll = async () => {
    setMergingAll(true);
    try {
      await mergeAll(studentId);
      fetchDocuments();
    } catch (err) {
      console.error('Merge all failed:', err);
    } finally {
      setMergingAll(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const activeCategory = allCategories[activeTab];
  const activeDocs = groupedDocs[activeCategory] || [];

  return (
    <Box>
      {/* Header with actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Documents
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canUploadDocuments && (
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<RequestIcon />}
              onClick={() => setRequestDialogOpen(true)}
            >
              Request Document
            </Button>
          )}
          {canMergeDocuments && documents.length > 0 && (
            <MergeButton
              onClick={handleMergeAll}
              loading={mergingAll}
              label="Merge All"
              variant="contained"
              size="small"
            />
          )}
          {student?.completeLoanFileUrl && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              href={student.completeLoanFileUrl}
              target="_blank"
              rel="noopener"
            >
              Complete Loan File
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Horizontal scrollable category bar */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 2,
          pb: 1,
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-track': { bgcolor: 'grey.100', borderRadius: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3, '&:hover': { bgcolor: 'grey.400' } },
        }}
      >
        {allCategories.map((cat, idx) => {
          const isExtra = cat === 'extra_documents';
          const count = getCatCount(cat);
          const label = DOCUMENT_CATEGORY_LABELS[cat] || cat;
          const uploaded = isExtra ? (count.count || 0) : count.uploaded;
          const total = isExtra ? null : count.total;
          const hasCompletePdf = count.hasCompletePdf;
          const isComplete = hasCompletePdf || (isExtra ? uploaded > 0 : uploaded === total);
          const isActive = activeTab === idx;
          const chipLabel = hasCompletePdf && uploaded === 0
            ? `${label}  PDF`
            : `${label}  ${isExtra ? uploaded : `${uploaded}/${total}`}`;

          return (
            <Chip
              key={cat}
              label={chipLabel}
              onClick={() => setActiveTab(idx)}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                flexShrink: 0,
                fontWeight: 600,
                fontSize: '0.8rem',
                height: 34,
                borderRadius: '17px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                ...(isActive
                  ? {
                      bgcolor: 'primary.main',
                      color: '#fff',
                      borderColor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' },
                      boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)',
                    }
                  : {
                      bgcolor: isComplete ? '#ecfdf5' : uploaded > 0 ? '#fffbeb' : '#fff',
                      color: isComplete ? '#059669' : uploaded > 0 ? '#d97706' : 'text.secondary',
                      borderColor: isComplete ? '#a7f3d0' : uploaded > 0 ? '#fde68a' : 'grey.300',
                      '&:hover': { bgcolor: isComplete ? '#d1fae5' : uploaded > 0 ? '#fef3c7' : 'grey.50' },
                    }),
              }}
            />
          );
        })}
      </Box>

      {/* Active category card */}
      <DocumentCategoryCard
        studentId={studentId}
        category={activeCategory}
        categoryData={student?.documentCategories?.[activeCategory] || {}}
        documents={activeDocs}
        onRefresh={fetchDocuments}
      />

      {/* Request Document Dialog */}
      <RequestDocumentDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        studentId={studentId}
        onSuccess={fetchDocuments}
      />
    </Box>
  );
};

export default DocumentCategoryGrid;
